import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDate } from "../utils";

import { usePymbleApi } from "./usePymbleApi";
import { TimetableType } from "./useTimetablePeriods";

// const MY_ONCALLS_END_POINT = `${api.dtAdmin}/lesson/oncall/`;
const MY_ONCALLS_END_POINT = "/timetable/admin/lesson/oncall/";

export type OnCallTeacher = {
  id?: string;
  _id: string;
  ContactId?: number;
  Name: string;
  LastCoverDate?: string;
  Department?: string;
  Email?: string;
};

/**
 * Hook for separate loading of on call teachers for a period
 * for components
 */
export function useOnCallTeachers(
  calendarDate: string,
  timetableType: TimetableType,
  timetablePeriod: string
) {
  const [myOnCallTeachers, setMyOnCallTeachers] = useState<any>([]);
  const [myOnCallTeachersLoaded, setMyOnCallTeachersLoaded] = useState(false);

  const queryClient = useQueryClient();

  const { callApiS2: callApi, isAuthenticated } = usePymbleApi();

  // Load the list from api
  const calDate = formatDate(calendarDate);
  let queryKey = [
    "my-on-call-teachers",
    calDate,
    timetableType,
    timetablePeriod,
  ];

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { isLoading, isError, data, error } = useQuery({
    queryKey,
    queryFn: () =>
      callApi(
        "GET",
        MY_ONCALLS_END_POINT +
          `?date=${calDate}&timetable_type=${timetableType}&period=${timetablePeriod}`,
      ),
    enabled: isAuthenticated,
    staleTime: 1000,
  });

  // When the data is loaded, set the state
  useEffect(() => {
    if (data && data.length) {
      let list = preProcessTeachers(data);

      // Sort by name
      list = sortTeachers(list);

      if (list) {
        setMyOnCallTeachers(list);
      }
      setMyOnCallTeachersLoaded(true);
    }
  }, [data]);

  const refreshMyOnCallTeachers = async () => {
    queryClient.invalidateQueries({ queryKey });
  };

  // Only sort by name at the moment
  const sortTeachers = (data: OnCallTeacher[]) => {
    return data.sort(function (a: OnCallTeacher, b: OnCallTeacher) {
      try {
        if (a && a?.Name && b && b?.Name) {
          // Sort by Date first
          var DateA = a?.LastCoverDate;
          var DateB = b?.LastCoverDate;
          if (DateA && DateB) {
            if (DateA < DateB) {
              return -1;
            }
            if (DateA > DateB) {
              return 1;
            }
          } else if (DateA && !DateB) {
            return 1;
          } else if (!DateA && DateB) {
            return -1;
          }
          // Sort by Name
          var NameA = a?.Name;
          var NameB = b?.Name;
          if (NameA < NameB) {
            return -1;
          }
          if (NameA > NameB) {
            return 1;
          }
        }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (e) {
        /* squash */
      }
      // names must be equal or documents incorrect
      return 0;
    });
  };

  // Decorate as required
  const preProcessTeachers = (classdata: any) => {
    return classdata.map((c: any) => {
      const t = { ...c, _id: c.id };
      return t;
    });
  };

  return {
    isError,
    error,
    myOnCallTeachers,
    refreshMyOnCallTeachers,
    myOnCallTeachersLoaded,
  };
}
